import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Save, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { parseHeight, parseAge } from "@/utils/heightParser";

interface UserStats {
  age?: number;
  sex?: string;
  height?: string;
  weight?: number;
  goalWeight?: number;
  goal?: string;
  specificGoal?: string;
  experience?: string;
  daysPerWeek?: number;
  minutesPerWorkout?: number;
  equipment?: string;
  injuries?: string;
  dietPreferences?: string;
}

interface StatsEditorProps {
  stats: UserStats;
  onSave: (stats: UserStats) => Promise<void>;
  userId?: string;
}

export function StatsEditor({ stats, onSave, userId }: StatsEditorProps) {
  const [localStats, setLocalStats] = useState<UserStats>(stats);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const [heightError, setHeightError] = useState<string>("");
  const [ageError, setAgeError] = useState<string>("");
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalStats(stats);
    setHasChanges(false);
  }, [stats]);

  // Auto-save after 2 seconds of no changes
  const triggerAutoSave = useCallback(async (statsToSave: UserStats) => {
    setIsSaving(true);
    try {
      await onSave(statsToSave);
      setHasChanges(false);
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    } catch (error) {
      toast.error("Failed to auto-save");
    } finally {
      setIsSaving(false);
    }
  }, [onSave]);

  const handleChange = (field: keyof UserStats, value: string | number) => {
    const newStats = { ...localStats, [field]: value };
    setLocalStats(newStats);
    setHasChanges(true);
    setAutoSaved(false);
    
    // Validate height if that's what changed
    if (field === 'height') {
      const result = parseHeight(value as string);
      setHeightError(result.valid ? "" : (result.error || ""));
      if (!result.valid && value) return;
    }
    
    // Validate age if that's what changed
    if (field === 'age') {
      const result = parseAge(value);
      setAgeError(result.valid ? "" : (result.error || ""));
      if (!result.valid && value) return;
    }
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new auto-save timer
    autoSaveTimerRef.current = setTimeout(() => {
      triggerAutoSave(newStats);
    }, 2000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(localStats);
      setHasChanges(false);
      toast.success("Stats updated successfully!");
    } catch (error) {
      toast.error("Failed to update stats");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Form */}
      <div className="grid grid-cols-2 gap-3">
        {/* Age */}
        <div className="space-y-1.5">
          <Label htmlFor="age" className="text-xs">Age</Label>
          <Input
            id="age"
            type="number"
            value={localStats.age || ''}
            onChange={(e) => handleChange('age', parseInt(e.target.value) || 0)}
            className={`h-9 ${ageError ? "border-destructive" : ""}`}
            placeholder="25"
          />
          {ageError && (
            <p className="text-[10px] text-destructive">{ageError}</p>
          )}
        </div>

        {/* Sex */}
        <div className="space-y-1.5">
          <Label htmlFor="sex" className="text-xs">Sex</Label>
          <Select
            value={localStats.sex || ''}
            onValueChange={(v) => handleChange('sex', v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Height */}
        <div className="space-y-1.5">
          <Label htmlFor="height" className="text-xs">Height</Label>
          <Input
            id="height"
            type="text"
            value={localStats.height || ''}
            onChange={(e) => handleChange('height', e.target.value)}
            className={`h-9 ${heightError ? "border-destructive" : ""}`}
            placeholder="5'10&quot;"
          />
          {heightError && (
            <p className="text-[10px] text-destructive">{heightError}</p>
          )}
        </div>

        {/* Weight */}
        <div className="space-y-1.5">
          <Label htmlFor="weight" className="text-xs">Current Weight (lbs)</Label>
          <Input
            id="weight"
            type="number"
            value={localStats.weight || ''}
            onChange={(e) => handleChange('weight', parseInt(e.target.value) || 0)}
            className="h-9"
            placeholder="185"
          />
        </div>

        {/* Goal Weight */}
        <div className="space-y-1.5">
          <Label htmlFor="goalWeight" className="text-xs">Goal Weight (lbs)</Label>
          <Input
            id="goalWeight"
            type="number"
            value={localStats.goalWeight || ''}
            onChange={(e) => handleChange('goalWeight', parseInt(e.target.value) || 0)}
            className="h-9"
            placeholder="175"
          />
        </div>

        {/* Days per week */}
        <div className="space-y-1.5">
          <Label htmlFor="daysPerWeek" className="text-xs">Days/Week</Label>
          <Select
            value={String(localStats.daysPerWeek || '')}
            onValueChange={(v) => handleChange('daysPerWeek', parseInt(v))}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7].map(d => (
                <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Minutes per workout */}
        <div className="space-y-1.5">
          <Label htmlFor="minutesPerWorkout" className="text-xs">Minutes/Workout</Label>
          <Select
            value={String(localStats.minutesPerWorkout || '')}
            onValueChange={(v) => handleChange('minutesPerWorkout', parseInt(v))}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15 min</SelectItem>
              <SelectItem value="30">30 min</SelectItem>
              <SelectItem value="45">45 min</SelectItem>
              <SelectItem value="60">60 min</SelectItem>
              <SelectItem value="75">75 min</SelectItem>
              <SelectItem value="90">90 min</SelectItem>
              <SelectItem value="120">120 min</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Experience */}
        <div className="space-y-1.5 col-span-2">
          <Label htmlFor="experience" className="text-xs">Experience</Label>
          <Select
            value={localStats.experience || ''}
            onValueChange={(v) => handleChange('experience', v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Goal */}
      <div className="space-y-1.5">
        <Label htmlFor="goal" className="text-xs">Primary Goal</Label>
        <Select
          value={localStats.goal || ''}
          onValueChange={(v) => handleChange('goal', v)}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Select goal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="lose-fat">Lose Fat</SelectItem>
            <SelectItem value="build-muscle">Build Muscle</SelectItem>
            <SelectItem value="get-stronger">Get Stronger</SelectItem>
            <SelectItem value="improve-cardio">Improve Cardio</SelectItem>
            <SelectItem value="general-fitness">General Fitness</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Specific Goal */}
      <div className="space-y-1.5">
        <Label htmlFor="specificGoal" className="text-xs">Specific Target</Label>
        <Input
          id="specificGoal"
          type="text"
          value={localStats.specificGoal || ''}
          onChange={(e) => handleChange('specificGoal', e.target.value)}
          className="h-9"
          placeholder="e.g., Get to 180 lbs, bench 225"
        />
      </div>

      {/* Equipment */}
      <div className="space-y-1.5">
        <Label htmlFor="equipment" className="text-xs">Equipment Access</Label>
        <Select
          value={localStats.equipment || ''}
          onValueChange={(v) => handleChange('equipment', v)}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Select equipment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full-gym">Full Gym</SelectItem>
            <SelectItem value="home-gym">Home Gym</SelectItem>
            <SelectItem value="dumbbells-only">Dumbbells Only</SelectItem>
            <SelectItem value="bodyweight">Bodyweight Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Injuries */}
      <div className="space-y-1.5">
        <Label htmlFor="injuries" className="text-xs">Injuries/Limitations</Label>
        <Textarea
          id="injuries"
          value={localStats.injuries || ''}
          onChange={(e) => handleChange('injuries', e.target.value)}
          className="min-h-[60px] resize-none"
          placeholder="Any injuries or limitations to consider..."
        />
      </div>


      {/* Sticky Save Button */}
      {hasChanges && (
        <div className="sticky bottom-0 bg-card/95 backdrop-blur-sm border-t border-border/50 -mx-4 px-4 py-3 mt-4">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Auto-saved indicator */}
      {autoSaved && !hasChanges && (
        <div className="flex items-center justify-center gap-2 text-xs text-green-500 py-2">
          <Check className="w-3 h-3" />
          <span>Auto-saved</span>
        </div>
      )}
    </div>
  );
}